import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AlertPopup from "../../components/common/AlertPopup"
import UserProfileTab from '../../components/UserProfileTab';
import { Box, FormGroup, Grid } from "@mui/material"
import {
  fetchProfile,
  selectOrgProfile,
  updateProfile,
  selectUpdateOrgProfileLoading,
} from "../../modules/orgProfile";
import { AppDispatch } from "../../store";
import PageCard from "../../components/PageCard";
import { StyledTabTitle } from "./styles";
import TabContext from "@mui/lab/TabContext";
import { TabList } from "@mui/lab";
import {
  ButtonGroupContainer,
  CancelButton,
  CreateProfileButton,
  CustomTab,
  CustomTabPanel,
  CustomTextField,
  Label
} from "../Worker/Create/styles";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProfileFormInputs } from "../../interfaces";
import * as yup from 'yup';
import SelectPlan from "../../components/SelectPlan";
import { useSidebar } from "../../context/SidebarContext";
import { useBreadcrumb } from '../../context/BreadcrumbContext';

const plans = [
  { label: "Enterprise Plus (12-Month)", value: "enterprise_plus" },
  { label: "Pro Monthly", value: "pro_monthly" },
  { label: "Free", value: "free" },
];

const schema = yup.object().shape({
  orgName: yup.string().required('Organization name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phoneNumber: yup.string().required('Phone number is required'),
  contactPerson: yup.string().required('Contact person is required'),
  designation: yup.string().required('Designation is required'),
  address: yup.string().required('Address is required'),
});

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector(selectOrgProfile);
  const updateLoading = useSelector(selectUpdateOrgProfileLoading);
  const { setPath, setIsShow } = useBreadcrumb();
  const { setActivePage } = useSidebar();

  const { handleSubmit, control, reset, formState: { errors } } = useForm<ProfileFormInputs>({
    resolver: yupResolver(schema) as any,
  });

  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('basic_details');
  const [plan, setPlan] = useState<string>("enterprise_plus");

  useEffect(() => {
    setPath([
      { title: 'Dashboard', path: '/' },
      { title: 'Profile', path: '' },
    ]);
    setActivePage('profile');
    setIsShow(true);
    dispatch(fetchProfile());
  }, [dispatch, setPath, setActivePage, setIsShow]);

  useEffect(() => {
    if (profile) {
      reset({
        orgName: profile.org_name,
        email: profile.user.email,
        phoneNumber: profile.contact_number || '',
        contactPerson: profile.contact_person || '',
        designation: profile.contact_person_designation || '',
        address: profile.user.address,
      });
    }
  }, [profile, reset]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: ProfileFormInputs) => {
    const payload = {
      org_name: data.orgName,
      email: data.email,
      contact_number: data.phoneNumber === '' ? null : data.phoneNumber,
      contact_person: data.contactPerson === '' ? null : data.contactPerson,
      contact_person_designation: data.designation === '' ? null : data.designation,
      address: data.address,
    };
    const result = await dispatch(updateProfile(payload));
    if (result.meta.requestStatus === 'fulfilled') {
      setAlert({ message: "Organization profile updated!", severity: 'success' });
    } else {
      setAlert({ message: "Failed to update organization profile.", severity: 'error' });
    }
  };



  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      {profile &&
        <PageCard padding={4} backgroundRequired={true}>
          <UserProfileTab
            user={profile.user}
            id={profile.org_id}
            status=""
          />
        </PageCard>
      }
      <PageCard padding={2} backgroundRequired={true}>
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example">
              <CustomTab label="Basic Details" value="basic_details" />
              <CustomTab label="Billing & Subscription" value="billing" />
            </TabList>
          </Box>
          <CustomTabPanel value="basic_details">
            <StyledTabTitle>
              Basic Details
            </StyledTabTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box padding={2}>
                <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Organization Name</Label>
                      <Controller
                        name="orgName"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            placeholder="Enter organization name"
                            error={!!errors.orgName}
                            helperText={errors.orgName?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                <Grid container spacing={2} marginBottom={2}>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Phone Number</Label>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            placeholder="Enter organization phone number"
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Email Address</Label>
                      <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            disabled
                            placeholder="Enter organization email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                <Grid container spacing={2} marginBottom={2}>
                  <Grid size={12}>
                    <FormGroup>
                      <Label>Address</Label>
                      <Controller
                        name="address"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            placeholder="Enter organization address"
                            error={!!errors.address}
                            helperText={errors.address?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                <Grid container spacing={2} marginBottom={2}>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Contact Person</Label>
                      <Controller
                        name="contactPerson"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            placeholder="Enter organization contact person"
                            error={!!errors.contactPerson}
                            helperText={errors.contactPerson?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Designation</Label>
                      <Controller
                        name="designation"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            placeholder="Enter contact person designation"
                            error={!!errors.designation}
                            helperText={errors.designation?.message}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Box>
              <ButtonGroupContainer>
                <CancelButton variant="outlined">
                  Cancel
                </CancelButton>
                <CreateProfileButton variant="contained" onClick={handleSubmit(onSubmit)} disabled={updateLoading}>
                  {updateLoading ? 'Updating...' : 'Update Organization Profile'}
                </CreateProfileButton>
              </ButtonGroupContainer>
            </form>
          </CustomTabPanel>
          <CustomTabPanel value="billing">
            <StyledTabTitle>
              Subscription Plan
            </StyledTabTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box padding={2}>
                <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                  <Grid size={6}>
                    <FormGroup>
                      <Label>Subscription Plan</Label>
                      <Controller
                        name="orgName"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <SelectPlan
                            value={plan}
                            options={plans}
                            onChange={(v) => setPlan(v)}
                          />
                        )}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Box>
            </form>
          </CustomTabPanel>
        </TabContext>
      </PageCard>
    </Box>
  )
}

export default ProfilePage;